import { appEnv } from 'app/core/environment';
import { logSentry } from 'app/core/errors/error-utils';
import { BLANK$ } from 'app/tools/utils';
import Geocode from 'react-geocode';
import { from, Observable, of } from 'rxjs';
import {
  catchError,
  map,
  pluck,
  switchMap,
  switchMapTo,
} from 'rxjs/operators';
import { ApiUrl } from '../api-core/api-constants';
import { NoContent, StatusResp } from '../api-core/api-core.types';
import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
} from '../api-core/api-request';
import apiItemHookFactory from '../api-core/utility-factories/api-item-hook-factory';
import apiRequestHookFactory from '../api-core/utility-factories/api-request-hook-factory';
import apiResourceHookFactory from '../api-core/utility-factories/api-resource-hook-factory';
import { apiRedux } from '../api-redux';
import {
  CreateRepairLocationReq,
  NotificationUpdateRequest,
  RepairAccountType,
  RepairLocation,
  RepairPersonalDetail,
} from '../repairer/api-repairer.types';
import { Identity, IdentityPlus } from '../user/api-user.types';
import { googleGeolocation$ } from '../utils-3rd-party/api-google-geolocation';
import {
  Address,
  CalendarData,
  Company,
  CompanyBankDetails,
  CompanyDetailsPt1,
  CreateCompanyReq,
  NotificationCount,
  NotificationData,
  RepairCapability,
  SetRepairCapabilitiesReq,
  UpdateCompanyReq,
  PageDetails,
} from './api-company.types';

export const useValidateCompanyDetailsPt1 = apiRequestHookFactory(
  apiRedux.companyDetailsPt1,
  (data: CompanyDetailsPt1): Observable<CompanyDetailsPt1> =>
    apiPost<CompanyDetailsPt1>(
      ApiUrl.VALIDATE_COMPANY_DETAILS_PT1,
      data
    )
);

export const useCreateCompany = apiRequestHookFactory(
  apiRedux.companyDetails,
  (data: CreateCompanyReq): Observable<Company> =>
    apiPost<Company>(ApiUrl.CREATE_BUSINESS, data)
);

const isCountryItem = (
  r: google.maps.GeocoderResult | google.maps.GeocoderAddressComponent
) => r.types.includes('country');

const addLocale$ = (identity: Identity): Observable<IdentityPlus> =>
  BLANK$.pipe(
    switchMapTo(googleGeolocation$),
    switchMap(googleGeolocation => {
      const { lat, lng } = googleGeolocation?.location || {};
      return from(
        Geocode.fromLatLng(lat, lng, appEnv.GOOGLE_API_KEY)
      );
    }),
    map(geocoding => {
      const item = geocoding.results
        .find(isCountryItem)
        ?.address_components.find(isCountryItem);
      return item?.short_name || item?.long_name;
    }),
    map(locale => ({ ...identity, locale })),
    catchError((e: unknown) => {
      logSentry(e);
      // fallback to just emitting `identity`
      return of(identity);
    })
  );

export const useIdentity = apiResourceHookFactory(
  apiRedux.identity,
  () => apiGet<Identity>(ApiUrl.IDENTITY).pipe(switchMap(addLocale$))
);

export const useUpdatePushNotification = apiRequestHookFactory(
  apiRedux.updatePushNotification,
  (data: NotificationUpdateRequest) =>
    apiPut<StatusResp>(ApiUrl.PUSH_NOTIFICATION, data).pipe()
);

export const useCurrentCompany = apiResourceHookFactory(
  apiRedux.currentCompanyDetails,
  () => apiGet<Company[]>(ApiUrl.CURRENT_COMPANY).pipe(pluck(0))
);

export const useCurrentCompanyBankDetails = apiResourceHookFactory(
  apiRedux.currentCompanyBankDetails,
  () =>
    apiGet<CompanyBankDetails>(
      ApiUrl.CURRENT_COMPANY_BANK_DETAILS
    ).pipe()
);

export const useDeleteCompanyBankDetails = apiRequestHookFactory(
  apiRedux.deleteCompanyBankDetails,
  () => apiDelete(ApiUrl.CURRENT_COMPANY_BANK_DETAILS).pipe()
);

export const useCreateCompanyAccountType = apiRequestHookFactory(
  apiRedux.setRepairAccountType,
  (data: RepairAccountType) =>
    apiPost<StatusResp>(ApiUrl.REPAIR_ACCOUNT_TYPE, data).pipe()
);

export const useCreateCompanyPersonalDetail = apiRequestHookFactory(
  apiRedux.setRepairPersonalDetail,
  (data: RepairPersonalDetail) =>
    apiPost<StatusResp>(ApiUrl.REPAIR_PERSONAL_DETAIL, data).pipe()
);
export const useUpdateCompanyPersonalDetail = apiRequestHookFactory(
  apiRedux.updateRepairPersonalDetail,
  (data: RepairPersonalDetail) =>
    apiPut<StatusResp>(ApiUrl.REPAIR_PERSONAL_DETAIL, data).pipe()
);
export const useGetCompanyPersonalDetail = apiResourceHookFactory(
  apiRedux.getRepairPersonalDetail,
  (): Observable<RepairPersonalDetail[]> =>
    apiGet<RepairPersonalDetail[]>(ApiUrl.REPAIR_PERSONAL_DETAIL)
);
export const useCreateCompanyBankDetails = apiRequestHookFactory(
  apiRedux.createCompanyBankDetails,
  (data: CompanyBankDetails) =>
    apiPost<CompanyBankDetails>(
      ApiUrl.CURRENT_COMPANY_BANK_DETAILS,
      data
    ).pipe()
);

export const useUpdateCompanyBankDetails = apiRequestHookFactory(
  apiRedux.updateCompanyBankDetails,
  (data: CompanyBankDetails) =>
    apiPut<CompanyBankDetails>(
      ApiUrl.CURRENT_COMPANY_BANK_DETAILS,
      data
    ).pipe()
);

export const useUpdateCurrentCompany = apiRequestHookFactory(
  apiRedux.updateCurrentCompanyDetails,
  (data: UpdateCompanyReq) =>
    apiPut<Company>(ApiUrl.CURRENT_COMPANY, data)
);

export const useCurrentRepairCapabilities = apiResourceHookFactory(
  apiRedux.currentRepairCapabilities,
  (): Observable<RepairCapability[]> =>
    apiGet<RepairCapability[]>(ApiUrl.CURRENT_REPAIR_CAPABILITIES)
);

export const useSetRepairPersonelAddress = apiRequestHookFactory(
  apiRedux.setRepairPersonelAddress,
  (data: Address): Observable<StatusResp> =>
    apiPost<StatusResp>(ApiUrl.REPAIRER_PERSONAL_ADDRESS, data)
);

export const useUpdateRepairPersonelAddress = apiRequestHookFactory(
  apiRedux.updateRepairPersonelAddress,
  (data: Address): Observable<StatusResp> =>
    apiPut<StatusResp>(ApiUrl.REPAIRER_PERSONAL_ADDRESS, data)
);

export const useGetRepairPersonelAddress = apiResourceHookFactory(
  apiRedux.getRepairPersonelAddress,
  (): Observable<Address> =>
    apiGet<Address>(ApiUrl.REPAIRER_PERSONAL_ADDRESS)
);

export const useSetRepairCapabilities = apiRequestHookFactory(
  apiRedux.setRepairCapabilities,
  (data: SetRepairCapabilitiesReq): Observable<RepairCapability[]> =>
    apiPost<RepairCapability[]>(ApiUrl.SET_REPAIR_CAPABILITIES, data)
);

export const useRepairLocations = apiResourceHookFactory(
  apiRedux.repairLocations,
  (): Observable<RepairLocation[]> =>
    apiGet<RepairLocation[]>(ApiUrl.REPAIR_LOCATIONS)
);

export const useCreateRepairLocation = apiRequestHookFactory(
  apiRedux.createRepairLocation,
  (data: CreateRepairLocationReq): Observable<RepairLocation> =>
    apiPost<RepairLocation>(ApiUrl.REPAIR_LOCATIONS, data)
);

export const useDeleteRepairLocation = apiRequestHookFactory(
  apiRedux.deleteRepairLocation,
  (id: number): Observable<NoContent> =>
    apiDelete(`${ApiUrl.REPAIR_LOCATIONS}/${id}`)
);

export const useCalendar = apiResourceHookFactory(
  apiRedux.calendarList,
  (): Observable<CalendarData[]> =>
    apiGet<CalendarData[]>(ApiUrl.CALENDAR_LIST)
);

export const useCalendarDetail = apiItemHookFactory(
  apiRedux.setCalendarDetail,
  (id: string) =>
    apiGet<CalendarData[]>(`${ApiUrl.CALENDAR_LIST}/${id}`)
);

export const useNotification = apiResourceHookFactory(
  apiRedux.setNotificationListData,
  (): Observable<NotificationData> =>
    apiGet<NotificationData>(ApiUrl.NOTIFICATION_LIST)
);

export const useNotificationCount = apiResourceHookFactory(
  apiRedux.getNotificationCount,
  (): Observable<NotificationCount> =>
    apiGet<NotificationCount>(ApiUrl.NOTIFICATION_COUNT)
);

export const useReadNotification = apiRequestHookFactory(
  apiRedux.readNotification,
  (id: number) =>
    apiPut<StatusResp>(`${ApiUrl.NOTIFICATION_COUNT}/${id}`)
);

export const useConsumerNotificationCount = apiResourceHookFactory(
  apiRedux.getConsumerNotificationCount,
  (): Observable<NotificationCount> =>
    apiGet<NotificationCount>(ApiUrl.CONSUMER_NOTIFICATION_COUNT)
);

export const useConsumerReadNotification = apiRequestHookFactory(
  apiRedux.readConsumerNotification,
  (id: number) =>
    apiPut<StatusResp>(`${ApiUrl.CONSUMER_NOTIFICATION_COUNT}/${id}`)
);

export const useRepairerReadNotification = apiRequestHookFactory(
  apiRedux.readRepairerNotification,
  (id: number) =>
    apiPut<StatusResp>(`${ApiUrl.NOTIFICATION_COUNT}/${id}`)
);

export const useSetRepairersActivity = apiRequestHookFactory(
  apiRedux.setRepairersActivity,
  (data: PageDetails): Observable<NoContent> =>
    apiPost<NoContent>(ApiUrl.REPAIRERS_ACTIVITY, data)
);
